import styled from "styled-components";
import { Card, CardContent } from "@interstate/components/Card";
import { OpenGraphMetadata } from "@coxauto-ui/cxm/communications/api";

const CARD_WIDTH = "320px";

const StyledThumbnail = styled.img`
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 4px;
  display: block;
`;

const StyledTitle = styled.span`
  display: block;
  width: 100%;
  font-size: 13px;
  font-weight: 600;
  color: #222;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledTitleLink = styled.a`
  display: block;
  width: 100%;
  font-size: 13px;
  font-weight: 600;
  color: #222;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: #0066cc;
  }
`;

const StyledDescription = styled.span`
  display: block;
  width: 100%;
  margin-top: 2px;
  font-size: 12px;
  color: #888;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Mirrors the email media-widget delete button in kendo-editor.tsx.
const StyledDismissButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid #ccc;
  background: #fff;
  color: #666;
  font-size: 14px;
  line-height: 18px;
  text-align: center;
  cursor: pointer;
  z-index: 1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);

  &:hover {
    background: #f44336;
    color: #fff;
    border-color: #f44336;
  }
`;

const StyledCardWrapper = styled.div`
  position: relative;
  margin-top: 8px;
  display: inline-block;
  width: min(${CARD_WIDTH}, 100%);
`;

// Two mutually-exclusive render shapes:
//   - Legacy OpenGraph fetch path passes `metadata`.
//   - Pending video endlinks (chip rail) pass flat `title`/`thumbnail`/`endLink`.
// `?: never` on the off-shape's fields makes mixing the two a compile error,
// so call sites have to pick one without overlap.
type LinkPreviewCardCommonProps = {
  onDismiss?: () => void;
  "data-testid"?: string;
};

type LinkPreviewCardMetadataProps = LinkPreviewCardCommonProps & {
  metadata: OpenGraphMetadata;
  title?: never;
  thumbnail?: never;
  endLink?: never;
};

type LinkPreviewCardFlatProps = LinkPreviewCardCommonProps & {
  metadata?: never;
  title: string;
  thumbnail?: string;
  endLink?: string;
};

export type LinkPreviewCardProps =
  | LinkPreviewCardMetadataProps
  | LinkPreviewCardFlatProps;

export function LinkPreviewCard({
  metadata,
  title,
  thumbnail,
  endLink,
  onDismiss,
  "data-testid": testId
}: LinkPreviewCardProps) {
  const displayTitle = title ?? metadata?.title ?? null;
  const displayThumbnail = thumbnail ?? metadata?.imageUrl ?? null;
  const displayUrl = endLink ?? metadata?.url ?? null;
  const displayDescription = metadata?.description ?? null;

  const thumbnailEl = displayThumbnail ? (
    <StyledThumbnail
      alt={displayTitle || "Link preview thumbnail"}
      loading="lazy"
      src={displayThumbnail}
      onError={e => {
        (e.target as HTMLImageElement).style.display = "none";
      }}
    />
  ) : undefined;

  return (
    <StyledCardWrapper>
      <Card
        content={
          <CardContent>
            {displayTitle &&
              (displayUrl ? (
                <StyledTitleLink
                  href={displayUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                  title={displayTitle}
                >
                  {displayTitle}
                </StyledTitleLink>
              ) : (
                <StyledTitle title={displayTitle}>{displayTitle}</StyledTitle>
              ))}
            {displayDescription && (
              <StyledDescription title={displayDescription}>
                {displayDescription}
              </StyledDescription>
            )}
          </CardContent>
        }
        data-testid={testId}
        media={thumbnailEl}
        mediaPosition="left"
        padding="8px"
        sx={{
          "& .card-header-content-container": {
            minWidth: 0,
            alignItems: "flex-start",
            textAlign: "left"
          },
          maxWidth: "100%",
          minWidth: 0
        }}
        width={CARD_WIDTH}
      />
      {onDismiss && (
        <StyledDismissButton
          aria-label="Dismiss link preview"
          data-testid={testId ? `${testId}-dismiss` : undefined}
          type="button"
          onClick={onDismiss}
        >
          {"×"}
        </StyledDismissButton>
      )}
    </StyledCardWrapper>
  );
}
